import { useEffect, useState } from 'react';
import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';
import MarkdownPreview from '@uiw/react-markdown-preview';
import './News.css';

interface NewsPost {
    _id: string;
    title: string;
    content: string;
    excerpt: string;
    previewImage: string | null;
    author: string;
    createdAt: string;
    updatedAt: string;
}

interface NewsResponse {
    news: NewsPost[];
    page: number;
    totalPages: number;
    total: number;
}

interface NewsProps {
    onClose?: () => void;
}

export default function News({ onClose }: NewsProps = {}) {
    const [news, setNews] = useState<NewsPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedNews, setSelectedNews] = useState<NewsPost | null>(null);

    useEffect(() => {
        fetchNews();
    }, []);

    const fetchNews = async () => {
        try {
            const response = await fetch('/api/news?limit=10');
            if (!response.ok) {
                throw new Error('Failed to fetch news');
            }
            const data: NewsResponse = await response.json();
            setNews(data.news);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load news');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('sr-RS', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    if (loading) {
        return <div className="news-container">Učitavanje vesti...</div>;
    }

    if (error) {
        return <div className="news-container">Greška: {error}</div>;
    }

    if (news.length === 0) {
        return null;
    }

    // Show full article view
    if (selectedNews) {
        return (
            <div className="news-container">
                <button
                    className="news-back-button"
                    onClick={() => {
                        setSelectedNews(null);
                        if (onClose) onClose();
                    }}
                >
                    ← Nazad
                </button>
                <article className="news-full-article">
                    <h1 className="news-article-title">{selectedNews.title}</h1>
                    <div className="news-meta">
                        <span className="news-author">{selectedNews.author}</span>
                        <span className="news-date">{formatDate(selectedNews.createdAt)}</span>
                    </div>
                    {selectedNews.previewImage && (
                        <div className="news-article-image-container">
                            <img
                                src={selectedNews.previewImage}
                                alt={selectedNews.title}
                                className="news-article-image"
                            />
                        </div>
                    )}
                    <div className="news-article-content">
                        <MarkdownPreview source={selectedNews.content} />
                    </div>
                </article>
            </div>
        );
    }

    // Show news grid/list
    return (
        <div className="news-container">
            <h2 className="news-title">Vesti</h2>
            <div className="news-grid">
                {news.map((post) => (
                    <div
                        key={post._id}
                        className="news-card"
                        onClick={() => setSelectedNews(post)}
                    >
                        {post.previewImage && (
                            <div className="news-image-container">
                                <img
                                    src={post.previewImage}
                                    alt={post.title}
                                    className="news-image"
                                />
                            </div>
                        )}
                        <div className="news-content">
                            <h3 className="news-post-title">{post.title}</h3>
                            {post.excerpt && (
                                <p className="news-excerpt">{post.excerpt}</p>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
